import React from 'react';
import { Loader2 } from 'lucide-react';

const variants = {
  primary: {
    base: {
      background: 'var(--gradient-gold)',
      color: 'white', // Always white text on gold buttons
      border: 'none',
    },
    hover: {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--shadow-gold)',
    },
    active: {
      transform: 'translateY(0)',
    },
    disabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  secondary: {
    base: {
      background: 'var(--gradient-premium)',
      color: 'white',
      border: 'none',
    },
    hover: {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--shadow-xl)',
    },
    active: {
      transform: 'translateY(0)',
    },
    disabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  outline: {
    base: {
      background: 'transparent',
      color: 'var(--gold)',
      border: '2px solid var(--gold)',
    },
    hover: {
      background: 'var(--gold-subtle)',
      transform: 'translateY(-1px)',
      boxShadow: 'var(--shadow-md)',
    },
    active: {
      transform: 'translateY(0)',
    },
    disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  ghost: {
    base: {
      background: 'transparent',
      color: 'var(--text)',
      border: '2px solid transparent',
    },
    hover: {
      background: 'var(--midnight-10)',
      borderColor: 'var(--border)',
    },
    active: {
      background: 'var(--midnight-20)',
    },
    disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  danger: {
    base: {
      background: 'var(--danger)',
      color: 'white',
      border: 'none',
    },
    hover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 32px rgba(198, 40, 40, 0.3)',
    },
    active: {
      transform: 'translateY(0)',
    },
    disabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
};

const sizes = {
  sm: {
    padding: '0.625rem 1.25rem',
    fontSize: 'var(--text-sm)',
    iconSize: 16,
  },
  md: {
    padding: '0.875rem 1.75rem',
    fontSize: 'var(--text-base)',
    iconSize: 18,
  },
  lg: {
    padding: '1.125rem 2.25rem',
    fontSize: 'var(--text-lg)',
    iconSize: 20,
  },
  xl: {
    padding: '1.375rem 2.75rem',
    fontSize: 'var(--text-xl)',
    iconSize: 24,
  },
};

const baseStyles = {
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    fontFamily: 'var(--font-body)',
    fontWeight: 600,
    letterSpacing: 'var(--tracking-widest)',
    textTransform: 'uppercase',
    borderRadius: 'var(--radius-lg)',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    outline: 'none',
    whiteSpace: 'nowrap',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shimmer: {
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
    transition: 'left 0.6s ease',
    pointerEvents: 'none',
  },
  shimmerHover: {
    left: '100%',
  },
  content: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  leftIcon,
  rightIcon,
  onClick,
  style = {},
  shimmer = true,
  ...props
}) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isActive, setIsActive] = React.useState(false);
  
  const variantStyles = variants[variant] || variants.primary;
  const sizeStyles = sizes[size] || sizes.md;
  const isDisabled = disabled || loading;
  
  const handleClick = (e) => {
    if (!isDisabled && onClick) {
      onClick(e);
    }
  };
  
  const buttonStyle = {
    ...baseStyles.button,
    ...variantStyles.base,
    ...sizeStyles,
    ...(fullWidth ? { width: '100%' } : {}),
    ...(isHovered && !isDisabled ? variantStyles.hover : {}),
    ...(isActive && !isDisabled ? variantStyles.active : {}),
    ...(isDisabled ? variantStyles.disabled : {}),
    ...style,
  };
  
  const renderIcon = (Icon, position) => {
    if (loading && position === 'left') {
      return (
        <div style={baseStyles.iconWrapper}>
          <Loader2 size={sizeStyles.iconSize} className="animate-spin" />
        </div>
      );
    }
    
    if (Icon && !loading) {
      return (
        <div style={baseStyles.iconWrapper}>
          {React.isValidElement(Icon) ? Icon : <Icon size={sizeStyles.iconSize} />}
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <button
      style={buttonStyle}
      disabled={isDisabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsActive(false);
      }}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      onClick={handleClick}
      {...props}
    >
      <div style={baseStyles.content}>
        {renderIcon(leftIcon, 'left')}
        {children}
        {renderIcon(rightIcon, 'right')}
      </div>
      
      {shimmer && variant === 'primary' && (
        <div 
          style={{
            ...baseStyles.shimmer,
            ...(isHovered && !isDisabled ? baseStyles.shimmerHover : {})
          }} 
        />
      )}
    </button>
  );
}

export default Button;